/**
 * Logique pure des encarts du dashboard admin (cohortes, inscriptions, sources).
 *
 * Comme `adminPlanGrouping.ts`, ce fichier n'importe RIEN de `wasp/server` :
 * son import validerait les variables d'environnement et ferait echouer les
 * tests unitaires au chargement du module.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Nombre de mois d'inscription affiches dans le tableau de cohortes. */
export const COHORT_MONTHS = 6;

/**
 * Delai d'inactivite au-dela duquel un utilisateur n'est plus considere comme
 * retenu. Aligne sur INACTIVE_AFTER_DAYS du reste du dashboard pour qu'un meme
 * utilisateur ne soit pas « inactif » dans une colonne et « retenu » ici.
 */
export const RETAINED_WITHIN_DAYS = 30;

export type CohortInput = {
  createdAt: Date;
  lastActiveAt: Date | null;
};

export type CohortRow = {
  /** Cle triable et stable, au format YYYY-MM. */
  month: string;
  signups: number;
  /** Inscrits de ce mois encore actifs dans les RETAINED_WITHIN_DAYS jours. */
  retained: number;
  /** Part retenue, 0 a 100, arrondie. 0 si le mois est vide. */
  retentionRate: number;
};

/** Cle de cohorte en UTC : deux admins dans deux fuseaux voient le meme mois. */
export function monthKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  return `${year}-${month}`;
}

function isRetained(lastActiveAt: Date | null, now: Date): boolean {
  // Un compte sans lastActiveAt n'a jamais ouvert l'app : il n'est pas retenu.
  // Le traiter comme actif gonflerait artificiellement toutes les cohortes.
  if (!lastActiveAt) return false;
  return now.getTime() - lastActiveAt.getTime() <= RETAINED_WITHIN_DAYS * MS_PER_DAY;
}

/**
 * Retention par mois d'inscription, du plus recent au plus ancien.
 *
 * Les mois sans aucune inscription sont emis quand meme (a zero) afin qu'un
 * trou dans l'acquisition reste visible au lieu de disparaitre du tableau.
 */
export function buildCohorts(
  users: CohortInput[],
  now: Date = new Date(),
  months: number = COHORT_MONTHS,
): CohortRow[] {
  const buckets = new Map<string, { signups: number; retained: number }>();

  // Amorcer les N derniers mois pour que les mois vides apparaissent.
  for (let i = 0; i < months; i += 1) {
    const cursor = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1),
    );
    buckets.set(monthKey(cursor), { signups: 0, retained: 0 });
  }

  for (const user of users) {
    const key = monthKey(new Date(user.createdAt));
    const bucket = buckets.get(key);
    // Les inscriptions plus anciennes que la fenetre sont ignorees : la ligne
    // n'existe pas, et en creer une casserait le "N derniers mois" annonce.
    if (!bucket) continue;
    bucket.signups += 1;
    if (isRetained(user.lastActiveAt, now)) {
      bucket.retained += 1;
    }
  }

  return [...buckets.entries()]
    .map(([month, { signups, retained }]) => ({
      month,
      signups,
      retained,
      retentionRate: signups === 0 ? 0 : Math.round((retained / signups) * 100),
    }))
    .sort((a, b) => b.month.localeCompare(a.month));
}

export type SignupCounts = {
  last7Days: number;
  last30Days: number;
};

/** Inscriptions sur les fenetres glissantes 7 et 30 jours. */
export function countRecentSignups(
  users: Pick<CohortInput, "createdAt">[],
  now: Date = new Date(),
): SignupCounts {
  let last7Days = 0;
  let last30Days = 0;

  for (const user of users) {
    const ageMs = now.getTime() - new Date(user.createdAt).getTime();
    if (ageMs <= 7 * MS_PER_DAY) last7Days += 1;
    if (ageMs <= 30 * MS_PER_DAY) last30Days += 1;
  }

  return { last7Days, last30Days };
}

export type SourceRow = {
  source: string;
  count: number;
  // `string | number` plutot que `unknown` : la contrainte Payload de SuperJSON
  // rejette `unknown` (voir adminUsersByPlan.ts). Ce module restant pur, on ne
  // peut pas importer SuperJSONValue depuis wasp/* sans casser les tests.
  [key: string]: string | number;
};

/** Libelle des inscrits sans UTM ni parrainage. */
export const DIRECT_SOURCE = "direct";

/**
 * Repartition des inscriptions par source d'acquisition, sur les 30 derniers
 * jours. Le parrainage prime sur l'UTM : un filleul arrive par un lien
 * d'ambassadeur, la source utm eventuelle n'est qu'un residu de navigation.
 */
export function buildSourceBreakdown(
  users: {
    createdAt: Date;
    utmSource: string | null;
    ambassadorRef: string | null;
  }[],
  now: Date = new Date(),
): SourceRow[] {
  const counts = new Map<string, number>();

  for (const user of users) {
    const ageMs = now.getTime() - new Date(user.createdAt).getTime();
    if (ageMs > 30 * MS_PER_DAY) continue;

    const ref = user.ambassadorRef?.trim();
    const utm = user.utmSource?.trim();
    const source = ref ? `ref:${ref}` : utm || DIRECT_SOURCE;
    counts.set(source, (counts.get(source) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source));
}
